/**
 * Sales Channels — CHANNEL ENTITY schemas + wire types.
 *
 * Three groups, in the order their migrations land: the channel itself
 * (create, rename/deactivate, card-strip list row), the SALES rung up on it
 * (`createChannelSaleSchema`, `editChannelSaleSchema`, `voidChannelSaleSchema`,
 * `ChannelSaleIndexRowWire`), and the batch PAYOUTS that settle them
 * (`createChannelPayoutSchema`, `voidChannelPayoutSchema`,
 * `ChannelPayoutRowWire`). They all belong in this one module; add to the
 * bottom rather than starting a second file.
 *
 * Feature-gated by the `sales_channels` flag on every API route
 * (`assertFeatureEnabled`) and in the web nav/route (`FeatureGate`).
 */
import { z } from "zod";
import { arLineItemSchema } from "./accountsReceivable.js";
import { calendarDateSchema } from "./dateWindow.js";
import { orgRelativeReturnPathSchema } from "./enableBanking.js";
// ---------------------------------------------------------------------------
// Write schemas — channel entity
// ---------------------------------------------------------------------------
/** Mirrors create_sales_channel's own validation (trim, non-empty, <= 200). */
const channelNameSchema = z
    .string()
    .trim()
    .min(1, "Channel name is required")
    .max(200, "Channel name must be 200 characters or fewer");
/**
 * POST /api/v1/sales-channels/orgs/:orgId/channels
 *
 * `kind` is NOT accepted: v1 has exactly one kind ('releve_basic', "Relevé
 * Basic POS") and the RPC pins it. External-provider kinds arrive with their
 * own credential fields, at which point this becomes a discriminated union.
 *
 * The clearing account is auto-provisioned by the RPC and is deliberately not
 * a client input — there is no clearing picker anywhere in the feature.
 */
export const createSalesChannelSchema = z
    .object({
    name: channelNameSchema,
})
    .strict();
/**
 * The "other Stripe payments" booking rule of a kind = 'stripe' channel: the
 * income account, VAT rate (an OUTPUT rate) and fund a charge books to when
 * it did not come from a Relevé payment link. Every member is nullable — a
 * NULL `revenue_account_id` leaves such charges waiting as NO_BOOKING_RULE.
 * Org scope (and the OUTPUT direction) is checked by the RPC
 * (`sales_channel_config_foreign_reference` → FOREIGN_REFERENCE).
 */
export const stripeChannelDefaultsSchema = z
    .object({
    revenue_account_id: z.string().uuid().nullable(),
    tax_rate_id: z.string().uuid().nullable(),
    fund_id: z.string().uuid().nullable(),
})
    .strict();
/**
 * PATCH /api/v1/sales-channels/orgs/:orgId/channels/:channelId
 *
 * Rename and/or (de)activate. An omitted field means "leave alone" — the RPC
 * reads NULL that way — so at least one must be present, otherwise the call is
 * a silent no-op the caller would read as success.
 *
 * There is no delete: `is_active: false` is the only retirement (the table
 * carries no DELETE RLS policy at all).
 *
 * kind = 'stripe' only (20261008100000): `payout_bank_account_id` /
 * `fee_account_id` (omitted = leave alone; both are required on a Stripe
 * channel, so they can be changed but never cleared) and `defaults`, the
 * "other Stripe payments" booking rule, written AS A GROUP — a `null` member
 * clears that default. Sent for a releve_basic channel, the RPC refuses
 * them (NOT_A_STRIPE_CHANNEL → 400), and the route additionally requires the
 * `sales_channels_stripe` flag whenever one of them is present.
 */
export const updateSalesChannelSchema = z
    .object({
    name: channelNameSchema.optional(),
    is_active: z.boolean().optional(),
    payout_bank_account_id: z.string().uuid().optional(),
    fee_account_id: z.string().uuid().optional(),
    defaults: stripeChannelDefaultsSchema.optional(),
})
    .strict()
    .refine((v) => v.name !== undefined ||
    v.is_active !== undefined ||
    v.payout_bank_account_id !== undefined ||
    v.fee_account_id !== undefined ||
    v.defaults !== undefined, { message: "Provide at least one field to change" });
/** True when a PATCH body touches a Stripe-only setting (the route's extra flag gate). */
export function updateTouchesStripeSettings(v) {
    return (v.payout_bank_account_id !== undefined ||
        v.fee_account_id !== undefined ||
        v.defaults !== undefined);
}
// ---------------------------------------------------------------------------
// Write schemas — channel SALES
// ---------------------------------------------------------------------------
/**
 * POST /api/v1/sales-channels/orgs/:orgId/sales — ring up one sale.
 *
 * Wraps `create_channel_sale`. Note what is NOT here, on purpose:
 *
 *  - **no `ar_account_id`**. The RPC resolves the org's AR control account
 *    itself (system_account_type = 'accounts_receivable', get_or_create_ar_account
 *    as fallback), so a sale and its later payout can never disagree about it.
 *    There is no AR field on the form either.
 *  - **no `status`**, **no `invoice_number`**: a rung sale is born 'sent' with
 *    the next number of the org-wide POS- series.
 *  - **no `due_date`**: it IS the sale date (cash and carry).
 *
 * `line_items` reuses the AR line shape verbatim (`arLineItemSchema`) because
 * the RPC's line loop is a copy of the AR one. An OMITTED `tax_inclusive` means
 * TRUE here (a counter price is a gross price) — the opposite of the manual AR
 * default, and the reason the form seeds its lines inclusive rather than
 * relying on the schema.
 */
export const createChannelSaleSchema = z
    .object({
    sales_channel_id: z.string().uuid(),
    /**
     * A calendar day, never an instant (docs/DATES.md):
     * `accounts_receivable.invoice_date` / `due_date` and
     * `ledger_transactions.transaction_date` are all `date` columns.
     * `calendarDateSchema` also rejects a well-formed non-day like 2026-02-31,
     * which would otherwise reach Postgres and 500 as a cast error.
     */
    sale_date: calendarDateSchema,
    /** Free text; the RPC falls back to 'POS sale' when absent or blank. */
    customer_name: z.string().nullable().optional(),
    reference: z.string().nullable().optional(),
    project_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
    line_items: z.array(arLineItemSchema).min(1),
})
    .strict();
/**
 * PATCH /api/v1/sales-channels/orgs/:orgId/sales/:invoiceId — edit one sale.
 *
 * Wraps `edit_channel_sale`, which REVERSES the current posting with a void
 * journal and re-posts the corrected sale under the SAME POS- number and the
 * same `accounts_receivable` row (docs/SALES_CHANNELS.md). The body is the
 * create body minus `sales_channel_id`: the channel is fixed for the life of
 * a sale. Every field is a full replacement — an omitted customer, reference,
 * project or fund is cleared, exactly as it would be absent on a new sale.
 */
export const editChannelSaleSchema = createChannelSaleSchema.omit({ sales_channel_id: true }).strict();
/**
 * POST /api/v1/sales-channels/orgs/:orgId/sales/:invoiceId/void
 *
 * Voiding posts a REVERSING journal — the sale keeps its POS- number and its
 * original posting forever (docs/AUDIT.md §7.3). 500 chars matches the RPC's
 * own sanitizer, which trims, strips control characters and refuses anything
 * longer.
 */
export const voidChannelSaleSchema = z
    .object({
    reason: z.string().max(500, "Reason must be 500 characters or fewer").optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Write schemas — batch PAYOUTS
// ---------------------------------------------------------------------------
/**
 * One settled sale inside a payout. There is no partial-allocation UI in v1 —
 * the dialog checkbox-selects whole sales and sends each one's full
 * `amount_outstanding` — but the wire carries the amount explicitly so the RPC
 * can re-validate it against the (possibly changed) outstanding balance under
 * its row lock rather than trusting the client's snapshot.
 *
 * `amount > 0` is the v1 shape. The deferred refund design turns refunds into
 * NEGATIVE allocations; that is the one place this bound is meant to be
 * relaxed, and never the AR-wide negative-total guard.
 */
export const channelPayoutAllocationSchema = z
    .object({
    ar_invoice_id: z.string().uuid(),
    /**
     * Whole cents only. `create_channel_payout` sums the RAW client amounts
     * into the settlement journal's clearing credit (a numeric(15,2)) while
     * each receipt rounds on insert, so a sub-cent allocation makes
     * round(SUM(x)) differ from SUM(round(x)) — the odd cent then sits in the
     * channel's clearing account forever, and `gross_amount` disagrees with the
     * allocations it is meant to total. The RPC is still the real boundary;
     * this closes the easy hole at the wire.
     */
    amount: z
        .number()
        .positive("Every allocated amount must be greater than zero")
        .multipleOf(0.01, "Amounts are in whole cents"),
})
    .strict();
/**
 * POST /api/v1/sales-channels/orgs/:orgId/payouts — record one batch payout.
 *
 * Wraps `create_channel_payout`. What is NOT here, on purpose:
 *
 *  - **no `clearing_account_id`**. The channel owns its clearing account and
 *    the RPC reads it off the channel row; there is no clearing picker
 *    anywhere in the feature.
 *  - **no `gross_amount` / `net_amount`**. Gross is SUM(allocations) and net is
 *    gross − fee, both derived server-side; sending them would invite a client
 *    and a ledger that disagree.
 *  - **no `payout_number`**: the org-wide POSP- series is allocated under the
 *    RPC's advisory lock.
 *
 * The fee account is required exactly when there is a fee — enforced here so
 * the form can show the error on the right field instead of round-tripping to
 * the RPC's FEE_ACCOUNT_REQUIRED envelope. The reverse direction (a fee account
 * supplied with a zero fee) is deliberately NOT an error: the dialog seeds its
 * fee picker from the channel's last payout before the operator has typed a
 * fee, so erroring there would block every zero-fee payout. The API strips the
 * account in that case, and the RPC + the
 * `channel_payouts_fee_account_required` CHECK keep the invariant in the
 * database either way.
 */
export const createChannelPayoutSchema = z
    .object({
    sales_channel_id: z.string().uuid(),
    /**
     * A calendar day, never an instant (docs/DATES.md): `payout_date` and the
     * `ledger_transactions.transaction_date` it drives are `date` columns.
     * `calendarDateSchema` also rejects a well-formed non-day like 2026-02-31,
     * which would otherwise reach Postgres and 500 as a cast error.
     */
    payout_date: calendarDateSchema,
    /** Where the deposit lands. Must resolve to the org currency (v1). */
    bank_account_id: z.string().uuid(),
    /** The processor's cut. 0 (the default) means no fee leg is posted. */
    fee_amount: z
        .number()
        .min(0, "The processing fee cannot be negative")
        // Whole cents, for the same reason as an allocation amount — and here a
        // sub-cent fee additionally unbalances the settlement journal (bank net =
        // gross - fee is rounded per leg), tripping the deferred balance trigger
        // at COMMIT with a message nobody can act on.
        .multipleOf(0.01, "The processing fee is in whole cents")
        .default(0),
    fee_account_id: z.string().uuid().nullable().optional(),
    allocations: z.array(channelPayoutAllocationSchema).min(1, "Select at least one sale to settle"),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.fee_amount > 0 && !v.fee_account_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["fee_account_id"],
            message: "Choose the account the processing fee is posted to",
        });
    }
});
/**
 * POST /api/v1/sales-channels/orgs/:orgId/payouts/:payoutId/void
 *
 * Voiding posts a REVERSING journal for every journal the payout created — the
 * N receipts and the settlement — and deletes nothing. The settled sales revert
 * to 'sent', which is what makes them voidable again. 500 chars matches the
 * RPC's own sanitizer.
 */
export const voidChannelPayoutSchema = z
    .object({
    reason: z.string().max(500, "Reason must be 500 characters or fewer").optional(),
})
    .strict();
/** Matches the `sales_channels.external_account_id` CHECK. */
export const STRIPE_ACCOUNT_ID_RE = /^acct_[A-Za-z0-9]{1,255}$/;
/**
 * POST /api/v1/sales-channels/orgs/:orgId/channels/stripe/connect
 *
 * Starts the install of the Relevé Stripe App (a public app with platform API
 * access, connected through its install link — plan Amendment A1). Nothing is
 * written: the channel config travels in the SIGNED `state`
 * (lib/signedState.ts, context "stripe-state") and the channel is created by
 * the CALLBACK, so an abandoned install leaves no orphan channel or clearing
 * account. `return_to` is the org-relative page the callback page sends the
 * user back to (the route asserts it is inside `:orgId`; the page re-asserts
 * the prefix before navigating).
 */
export const connectStripeChannelSchema = z
    .object({
    name: channelNameSchema,
    payout_bank_account_id: z.string().uuid(),
    fee_account_id: z.string().uuid(),
    defaults: stripeChannelDefaultsSchema.optional(),
    return_to: orgRelativeReturnPathSchema,
})
    .strict();
/**
 * POST /api/v1/sales-channels/orgs/:orgId/channels/stripe/callback — the
 * `/stripe-callback` page posts what Stripe's install redirect carried:
 * `state` (ours, signed), `user_id`, `account_id`, `install_signature`, and
 * `livemode` (Stripe appends `livemode=false` for a test/sandbox install and
 * nothing for live mode — send `false` only then; the raw query value
 * `"false"`/`"true"` is accepted too). The route checks, in order: the state
 * (signature, expiry, org = `:orgId`, user = caller), the install signature
 * (the app's signing secret over `{state, user_id, account_id}`), and that
 * the install's mode matches the server's key; then creates — or reconnects —
 * the channel. A cancelled install (`error=access_denied`) never reaches this
 * route: the page shows its cancelled state.
 */
export const stripeChannelCallbackSchema = z
    .object({
    state: z.string().min(1).max(4096),
    user_id: z.string().min(1).max(255),
    account_id: z.string().regex(STRIPE_ACCOUNT_ID_RE),
    install_signature: z.string().min(1).max(4096),
    livemode: z
        .union([z.boolean(), z.enum(["true", "false"])])
        .transform((value) => value === true || value === "true")
        .optional(),
})
    .strict();
/** Error codes the connect / callback routes answer with (`{ error, code }`). */
export const STRIPE_CHANNEL_ERROR_CODES = {
    /** The account is on another org's channel, or on a channel still connected. 409. */
    externalAccountInUse: "EXTERNAL_ACCOUNT_IN_USE",
    /** A channel with that name already exists in the org. 400. */
    duplicateName: "DUPLICATE_NAME",
    /** The signed state expired (30 minutes). 400. */
    stateExpired: "STRIPE_STATE_EXPIRED",
    /** The signed state is malformed or forged. 400. */
    stateInvalid: "STRIPE_STATE_INVALID",
    /** The install redirect's `install_signature` does not verify (forged, tampered or stale). 400. */
    installSignatureInvalid: "STRIPE_INSTALL_SIGNATURE_INVALID",
    /** A test/sandbox install on a live server, or a live install on a test server. 400. */
    modeMismatch: "MODE_MISMATCH",
};
// ---- The feed (inbox) -------------------------------------------------------
export const SALES_CHANNEL_FEED_EVENT_TYPES = [
    "charge.succeeded",
    "charge.refunded",
    "payout.reconciliation_completed",
];
export const SALES_CHANNEL_FEED_STATUSES = [
    "pending",
    "processed",
    "needs_attention",
    "dismissed",
];
/** Mirrors the `sales_channel_external_events.reason_code` CHECK (i18n: salesChannels.feed.reasons.<CODE>). */
export const SALES_CHANNEL_FEED_REASON_CODES = [
    "NO_BOOKING_RULE",
    "CURRENCY",
    "MANUAL_PAYOUT",
    "REFUND_IN_PAYOUT",
    "UNBOOKED_CHARGE",
    "MISMATCH",
    "REFUND",
    "RPC_FAILED",
];
/** POST /api/v1/sales-channels/orgs/:orgId/feed-events/:eventId/status */
export const salesChannelFeedEventStatusSchema = z
    .object({ action: z.enum(["retry", "dismiss"]) })
    .strict();
// ---- The webhook normaliser (PII whitelist) ---------------------------------
/**
 * The Stripe event types the webhook acts on: the three the feed books, plus
 * `account.application.deauthorized` (the org — or Stripe — revoked the
 * platform's access; the channel goes `disconnected`). Anything else is
 * acknowledged (200) and dropped.
 */
export const STRIPE_WEBHOOK_EVENT_TYPES = [
    ...SALES_CHANNEL_FEED_EVENT_TYPES,
    "account.application.deauthorized",
];
const stripeIdSchema = z.string().min(1).max(255);
const stripeMinorAmountSchema = z.number().int().safe();
const stripeUnixSecondsSchema = z.number().int().nonnegative();
const stripeCurrencySchema = z
    .string()
    .regex(/^[A-Za-z]{3}$/)
    .transform((value) => value.toLowerCase());
/** The envelope fields we read off EVERY event (snapshot payload, Connect). */
const stripeEventEnvelopeSchema = z.object({
    id: stripeIdSchema,
    type: z.string().min(1).max(255),
    /** Present on Connect events: the connected account the event happened on. */
    account: z.string().regex(STRIPE_ACCOUNT_ID_RE).nullable().optional(),
    created: stripeUnixSecondsSchema,
    data: z.object({ object: z.record(z.unknown()) }),
});
/**
 * The charge fields the feed may keep. NOTHING about the buyer — no
 * billing_details, receipt_email, customer, payment_method_details, shipping:
 * Stripe keeps those (GDPR_COMPLIANCE.md PII inventory). `description` is
 * merchant-typed free text (treated like ledger_transactions.description).
 * Only ONE metadata key survives: `releve_payment_link_id` (Layer 2's booking
 * rule).
 */
const stripeChargeSchema = z.object({
    id: stripeIdSchema,
    amount: stripeMinorAmountSchema,
    amount_refunded: stripeMinorAmountSchema.optional(),
    currency: stripeCurrencySchema,
    created: stripeUnixSecondsSchema,
    description: z.string().nullable().optional(),
    payment_intent: z.union([z.string(), z.object({ id: z.string() }).passthrough()]).nullable().optional(),
    metadata: z.record(z.unknown()).nullable().optional(),
});
const stripePayoutSchema = z.object({
    id: stripeIdSchema,
    amount: stripeMinorAmountSchema,
    currency: stripeCurrencySchema,
    created: stripeUnixSecondsSchema,
    arrival_date: stripeUnixSecondsSchema,
    automatic: z.boolean(),
    status: z.string().max(64).optional(),
    reconciliation_status: z.string().max(64).optional(),
});
const stripeBalanceTransactionSchema = z.object({
    id: stripeIdSchema,
    type: z.string().min(1).max(64),
    source: z
        .union([z.string().max(255), z.object({ id: z.string().max(255) }).passthrough()])
        .nullable()
        .optional(),
    amount: stripeMinorAmountSchema,
    fee: stripeMinorAmountSchema,
    net: stripeMinorAmountSchema,
    currency: stripeCurrencySchema,
});
/**
 * Whitelist balance transactions (`GET /v1/balance_transactions?payout=…`).
 * Returns null when ANY item fails the shape — the caller treats that as an
 * enrichment failure (5xx, Stripe retries), never as a payout with lines
 * silently dropped.
 */
export function normalizeStripeBalanceTransactions(raw) {
    const out = [];
    for (const item of raw) {
        const parsed = stripeBalanceTransactionSchema.safeParse(item);
        if (!parsed.success)
            return null;
        const bt = parsed.data;
        out.push({
            id: bt.id,
            type: bt.type,
            source: bt.source == null ? null : typeof bt.source === "string" ? bt.source : bt.source.id,
            amount: bt.amount,
            fee: bt.fee,
            net: bt.net,
            currency: bt.currency,
        });
    }
    return out;
}
const isWebhookEventType = (value) => STRIPE_WEBHOOK_EVENT_TYPES.includes(value);
/** The longest description kept (the inbox payload is not a document store). */
export const STRIPE_DESCRIPTION_MAX = 1000;
/**
 * Turn a VERIFIED Stripe webhook body (already JSON-parsed) into the inbox
 * record — the PII whitelist. Only the fields listed in the schemas above
 * survive; everything else Stripe sent is dropped here and never reaches the
 * database, Sentry or a log line.
 */
export function normalizeStripeWebhookEvent(raw) {
    const envelope = stripeEventEnvelopeSchema.safeParse(raw);
    if (!envelope.success)
        return { outcome: "invalid", reason: "not_a_stripe_event" };
    const ev = envelope.data;
    if (!isWebhookEventType(ev.type))
        return { outcome: "ignore", reason: "unsupported_event_type" };
    // A platform-account event (no `account`) is never a connected channel's.
    if (!ev.account)
        return { outcome: "ignore", reason: "not_a_connect_event" };
    const base = {
        account_id: ev.account,
        provider_event_id: ev.id,
        event_type: ev.type,
        occurred_at: new Date(ev.created * 1000).toISOString(),
    };
    if (ev.type === "account.application.deauthorized") {
        const appId = typeof ev.data.object.id === "string" ? ev.data.object.id.slice(0, 255) : null;
        return {
            outcome: "record",
            event: { ...base, object_id: appId || ev.account, payload: {} },
        };
    }
    if (ev.type === "charge.succeeded" || ev.type === "charge.refunded") {
        const charge = stripeChargeSchema.safeParse(ev.data.object);
        if (!charge.success)
            return { outcome: "invalid", reason: "unreadable_charge" };
        const ch = charge.data;
        const linkId = ch.metadata?.releve_payment_link_id;
        const description = ch.description ? ch.description.slice(0, STRIPE_DESCRIPTION_MAX) : null;
        const paymentIntent = ch.payment_intent == null
            ? null
            : typeof ch.payment_intent === "string"
                ? ch.payment_intent
                : ch.payment_intent.id;
        const payload = {
            id: ch.id,
            object: "charge",
            amount: ch.amount,
            currency: ch.currency,
            created: ch.created,
            description,
            payment_intent: paymentIntent,
            ...(ev.type === "charge.refunded" && ch.amount_refunded !== undefined
                ? { amount_refunded: ch.amount_refunded }
                : {}),
            ...(typeof linkId === "string" && linkId.length <= 64
                ? { metadata: { releve_payment_link_id: linkId } }
                : {}),
        };
        return { outcome: "record", event: { ...base, object_id: ch.id, payload } };
    }
    // payout.reconciliation_completed
    const payout = stripePayoutSchema.safeParse(ev.data.object);
    if (!payout.success)
        return { outcome: "invalid", reason: "unreadable_payout" };
    const po = payout.data;
    const payload = {
        id: po.id,
        object: "payout",
        amount: po.amount,
        currency: po.currency,
        created: po.created,
        arrival_date: po.arrival_date,
        automatic: po.automatic,
        ...(po.status !== undefined ? { status: po.status } : {}),
        ...(po.reconciliation_status !== undefined
            ? { reconciliation_status: po.reconciliation_status }
            : {}),
        // Stripe lists a payout's balance transactions for AUTOMATIC payouts only
        // (`?payout=` filter); a manual one is recorded with none and the sweep
        // sets it aside as MANUAL_PAYOUT.
        ...(po.automatic ? {} : { balance_transactions: [] }),
    };
    return {
        outcome: "record",
        event: { ...base, object_id: po.id, payload },
        needs_balance_transactions: po.automatic,
    };
}
// ---------------------------------------------------------------------------
// Stripe payment links + QR codes (Layer 2, 20261008110000; docs/SALES_CHANNELS.md
// "Stripe kind"). Routes: GET / POST …/channels/:channelId/payment-links and
// PATCH …/channels/:channelId/payment-links/:linkId — gated like every Stripe
// route (sales_channels → sales_channels_stripe 403, then 503 when the
// backend has no Stripe platform configured).
// ---------------------------------------------------------------------------
/**
 * `donation` → Stripe `submit_type=donate` (the "Donate" button,
 * donate.stripe.com); `payment` → `submit_type=pay`. Immutable (it is
 * Stripe's submit_type).
 */
export const SALES_CHANNEL_PAYMENT_LINK_PURPOSES = ["donation", "payment"];
/** Mirrors the `sales_channel_payment_links_label_check` CHECK (trim, 1–200). */
const paymentLinkLabelSchema = z
    .string()
    .trim()
    .min(1, "Label is required")
    .max(200, "Label must be 200 characters or fewer");
/**
 * A positive amount in MAJOR units of the org currency with at most two
 * decimals — the column is numeric(15,2). The route additionally refuses
 * decimals a zero-decimal currency (JPY, …) cannot carry.
 */
const paymentLinkAmountSchema = z
    .number()
    .finite()
    .positive("Amount must be greater than zero")
    .max(9_999_999_999_999.99)
    .refine((v) => Math.abs(Math.round(v * 100) - v * 100) < 1e-6, {
    message: "Amount can have at most two decimals",
});
/**
 * The booking settings of a link — the income account (required), VAT rate
 * (an OUTPUT rate; null = none) and fund. Org scope is checked by the RPC
 * (FOREIGN_REFERENCE). Editable; an edit applies to charges booked from then on.
 */
export const salesChannelPaymentLinkBookingSchema = z
    .object({
    revenue_account_id: z.string().uuid(),
    tax_rate_id: z.string().uuid().nullable(),
    fund_id: z.string().uuid().nullable(),
})
    .strict();
/**
 * POST /api/v1/sales-channels/orgs/:orgId/channels/:channelId/payment-links
 *
 * `amount` null = the payer chooses (Stripe `custom_unit_amount`), with an
 * optional `preset_amount` the Stripe page pre-fills; a preset beside a fixed
 * amount is refused. Always the org currency (there is no currency input).
 * Amounts are IMMUTABLE once created — to change one, create a new link.
 */
export const createSalesChannelPaymentLinkSchema = z
    .object({
    label: paymentLinkLabelSchema,
    purpose: z.enum(SALES_CHANNEL_PAYMENT_LINK_PURPOSES),
    amount: paymentLinkAmountSchema.nullable(),
    preset_amount: paymentLinkAmountSchema.nullable().optional(),
    revenue_account_id: z.string().uuid(),
    tax_rate_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
})
    .strict()
    .refine((v) => v.amount === null || v.preset_amount == null, {
    message: "A preset amount is only allowed when the payer chooses the amount",
    path: ["preset_amount"],
});
/**
 * PATCH /api/v1/sales-channels/orgs/:orgId/channels/:channelId/payment-links/:linkId
 *
 * The label, the booking settings (written AS A GROUP — the channel
 * `defaults` shape; a null VAT rate / fund clears it) and deactivation
 * (`is_active: false` only: Stripe deactivates first, then the row; a
 * deactivated link is never reactivated — create a new one). There is no
 * amount / purpose field: `.strict()` refuses them (immutable). At least one
 * field must be present.
 */
export const updateSalesChannelPaymentLinkSchema = z
    .object({
    label: paymentLinkLabelSchema.optional(),
    booking: salesChannelPaymentLinkBookingSchema.optional(),
    is_active: z.literal(false).optional(),
})
    .strict()
    .refine((v) => v.label !== undefined || v.booking !== undefined || v.is_active !== undefined, {
    message: "Provide at least one field to change",
});
/**
 * Coded refusals of the payment-link routes (`{ error, code }`). The RPC's own
 * codes (CHANNEL_NOT_CONNECTED, NOT_A_STRIPE_CHANNEL, FOREIGN_REFERENCE,
 * CANNOT_REACTIVATE, INVALID_AMOUNT, …) pass through as 400s with the same
 * `code`.
 */
export const STRIPE_PAYMENT_LINK_ERROR_CODES = {
    /** The channel is not a Stripe channel. 400. */
    notAStripeChannel: "NOT_A_STRIPE_CHANNEL",
    /** The channel is not connected / active. 400. */
    channelNotConnected: "CHANNEL_NOT_CONNECTED",
    /** The org has no currency, or the amount has more decimals than the currency carries. 400. */
    invalidAmount: "INVALID_AMOUNT",
    /** Stripe refused the price / link (e.g. below Stripe's minimum charge). 400. */
    stripeRejected: "STRIPE_REJECTED",
};
//# sourceMappingURL=salesChannels.js.map